import { APIEndpoints } from '@/@admin/types/constants';
import {
    AdminToolsParams,
    Opportunity,
    SubmitFundsResponse,
    UpdateResponse,
} from '@/@admin/types/entities';
import { buildCacheKey, normalizeSalesforceData } from '@/@admin/utils';
import { ERROR_MESSAGES } from '@/@cms/types';
import { SalesforceClient } from '@/@projects/types/entities';
import { MicroserviceAPIEndpoints } from '@/@studio/types/constants';
import { tracking } from '@/lib/features/tracking';
import { studioApi, portalApi, microserviceApi } from '@/lib/http/axios';
import { BaseService } from './base.service';

interface SalesforceOpportunityRecord {
    Id: string;
    Name: string;
    StageName: string;
    CloseDate: string;
    Account?: { Name: string };
}

/**
 * Service class for Salesforce operations.
 * Client/opportunity searches use microserviceApi (mfour-studio-service-salesforce).
 * Contact search remains on studioApi until a microservice endpoint is available.
 * License submission uses portalApi (mfour-studio-api).
 * Deposit form uses studioApi.
 */
export class SalesforceService extends BaseService {
    constructor() {
        super(studioApi);
    }

    async getSfClients(params: AdminToolsParams): Promise<SalesforceClient[]> {
        try {
            if (!params.searchTerm) return [];

            const cacheKey = buildCacheKey('adminFetchSfClients', params);
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await microserviceApi.get(
                MicroserviceAPIEndpoints.CLIENT_SEARCH,
                {
                    params: { name: params.searchTerm },
                },
            );

            const normalized: SalesforceClient[] = (response?.matches || []).map(
                (match: { id: string; name: string }) => ({
                    id: match.id,
                    name: match.name,
                }),
            );

            this.setCachedData(cacheKey, normalized);
            return normalized;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get Salesforce clients: ${errorMessage}`),
                'studio.SalesforceService.getSfClients',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get Salesforce clients: ${errorMessage}`),
            );
        }
    }

    async getSfContacts(params: AdminToolsParams): Promise<SalesforceClient[]> {
        try {
            const cacheKey = buildCacheKey('adminFetchSfContacts', params);
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get(APIEndpoints.GET_SF_CONTACTS, {
                params,
            });

            if ((response as { hasError: boolean; message?: string })?.hasError) {
                tracking.errorWithSource(
                    new Error(
                        (response as { hasError: boolean; message?: string })?.message ||
                            'Salesforce API returned an error',
                    ),
                    'studio.SalesforceService.getSfContacts',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(
                        (response as { hasError: boolean; message?: string })?.message ||
                            'Salesforce API error',
                    ),
                );
            }

            const normalized = normalizeSalesforceData(response);

            this.setCachedData(cacheKey, normalized);
            return normalized;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get Salesforce contacts: ${errorMessage}`),
                'studio.SalesforceService.getSfContacts',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get Salesforce contacts: ${errorMessage}`),
            );
        }
    }

    async getSfOpportunities(params: AdminToolsParams): Promise<SalesforceClient[]> {
        try {
            if (!params.sfAccountId && !params.searchTerm) return [];

            const cacheKey = buildCacheKey('adminFetchSfOpportunities', params);
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await microserviceApi.get(
                MicroserviceAPIEndpoints.OPPORTUNITY_SEARCH,
                {
                    params: {
                        name: params.searchTerm || '',
                        accountId: params.sfAccountId || '',
                    },
                },
            );

            const normalized: SalesforceClient[] = (response?.records || []).map(
                (record: SalesforceOpportunityRecord) => ({
                    id: record.Id,
                    name: record.Name,
                }),
            );

            this.setCachedData(cacheKey, normalized);
            return normalized;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get Salesforce opportunities: ${errorMessage}`),
                'studio.SalesforceService.getSfOpportunities',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to get Salesforce opportunities: ${errorMessage}`),
            );
        }
    }

    async getSfOpportunityById(opportunityId: string): Promise<Opportunity> {
        try {
            const cacheKey = buildCacheKey('adminFetchSfOpportunityById', { opportunityId });
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await microserviceApi.get(
                `${MicroserviceAPIEndpoints.OPPORTUNITY}/${opportunityId}`,
            );

            if (!response?.opportunity) {
                tracking.errorWithSource(
                    new Error('No opportunity data received from the server'),
                    'studio.SalesforceService.getSfOpportunityById',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR('No opportunity data received from the server'),
                );
            }

            const opp = response.opportunity;
            const result: Opportunity = {
                amount: parseFloat(opp.Amount) || 0,
                contacts: [],
                startDate: opp.Studio_Seat_Start_Date__c || '',
                endDate: opp.Studio_Seat_End_Date__c || '',
                stage: opp.StageName || '',
                planName: opp.License_level__c || '',
                numberOfSeats: 0,
            };

            this.setCachedData(cacheKey, result);
            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get Salesforce opportunity by ID: ${errorMessage}`),
                'studio.SalesforceService.getSfOpportunityById',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(
                    `Failed to get Salesforce opportunity by ID: ${errorMessage}`,
                ),
            );
        }
    }

    async submitClientLicense(
        clientId: string,
        opportunityId: string,
        contactEmail?: string,
    ): Promise<UpdateResponse> {
        try {
            const { data: response } = await portalApi.post(APIEndpoints.CLIENT_LICENSE, {
                opportunityId,
                clientId,
                contactEmail,
            });

            // Ensure we have a valid response
            if (response.hasError) {
                tracking.errorWithSource(
                    new Error('Client license submission failed.'),
                    'studio.SalesforceService.submitClientLicense',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(
                        response.message || 'Client license submission failed.',
                    ),
                );
            }

            return response;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to submit client license: ${errorMessage}`),
                'studio.SalesforceService.submitClientLicense',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to submit client license: ${errorMessage}`),
            );
        }
    }

    async submitDepositForm(data: FormData): Promise<SubmitFundsResponse> {
        try {
            const { data: response } = await this.axiosInstance.post(
                APIEndpoints.SUBMIT_DEPOSIT_FORM,
                data,
            );
            // Ensure we have a valid response
            if (response.hasError) {
                tracking.errorWithSource(
                    new Error('Deposit form submission failed.'),
                    'studio.SalesforceService.submitDepositForm',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(response.message || 'Deposit form submission failed.'),
                );
            }
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to submit deposit form: ${errorMessage}`),
                'studio.SalesforceService.submitDepositForm',
            );
            throw new Error(ERROR_MESSAGES.API_ERROR(errorMessage));
        }
    }
}

export const salesforceService = new SalesforceService();
